import './NotifyItem.sass';

import React, { FC, memo, ReactNode, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router5';

import { NotifyItemProps } from '$shared/components/NotifyItem/NotifyItem.interface';
import { postsSelector } from '$store/posts';
import { mainSelector } from '$store/main';
import {
  classNames,
  formatCurrency,
  genderText,
  getImageFromPost,
  parsePostText,
  transliterate,
} from '$utils';
import {
  useCurrentLang,
  useCurrentUserInfo,
  useDateShort,
  useDesktop,
  useTranslator,
} from '$hooks';
import { Attachments, ModalLink, Truncate } from '$shared/components';
import {
  CHAT,
  PROFILE,
  SESSIONS,
  SUBSCRIPTION_ADULT,
  SUBSCRIPTION_AUTHOR,
} from '$shared/constants/pages';
import {
  AttachItem,
  AttachPhoto,
  AttachRouletteLot,
  AttachType,
  CurrencyEnum,
  NotifyModelType,
  PostModel,
  SubscriptionType,
} from '$store/models';
import {
  IcActionTypeComment,
  IcActionTypeErotic,
  IcActionTypeLike,
  IcActionTypeLogin,
  IcActionTypeMoney,
  IcActionTypeSponsor,
  IcAdd28,
  IcAngleRight16,
  IcBubble24,
  IcClose28,
} from '$assets';
import { followersActions, followersSelector } from '$store/followers';
import { Button } from '$uikit';
import { incline } from 'lvovich';
import { LanguageTags } from '$shared/constants';
import { goTo, showModal } from '$navigation/helper';
import { notesSelector } from '$store/notes';

const NotifyItemIconComponent: FC<{ type: NotifyModelType }> = ({ type }) => {
  const icon =
    type === NotifyModelType.LIKE ? (
      <IcActionTypeLike />
    ) : type === NotifyModelType.COMMENT ? (
      <IcActionTypeComment />
    ) : type === NotifyModelType.NEW_SUBSCRIBERS ? (
      <IcActionTypeSponsor />
    ) : type === NotifyModelType.NEW_DEVICE ? (
      <IcActionTypeLogin />
    ) : type === NotifyModelType.ENDED_SUBSCRIPTION ? (
      <IcActionTypeErotic />
    ) : [
        NotifyModelType.SOLD_MESSAGES,
        NotifyModelType.TIPS,
        NotifyModelType.ROULETTE_MESSAGES,
        NotifyModelType.BUNDLE_SOLD,
        NotifyModelType.GIFT_SOLD,
      ].includes(type) ? (
      <IcActionTypeMoney />
    ) : null;

  return icon ? <div className="NotifyItem__icon">{icon}</div> : null;
};

const NotifyItemIcon = memo(NotifyItemIconComponent);

export const NotifyItem: FC<NotifyItemProps> = ({ notify }) => {
  const dispatch = useDispatch();
  const { posts } = useSelector(postsSelector);
  const { users } = useSelector(mainSelector);
  const { following } = useSelector(followersSelector);
  const t = useTranslator();
  const curUser = useCurrentUserInfo();
  const { decryptKeys } = useSelector(mainSelector);
  const isDesktop = useDesktop();
  const { notes } = useSelector(notesSelector);

  const date = useDateShort(notify.createdAt);

  const handleFollow = useCallback(
    (isFollowed, userId) => () => {
      if (!isFollowed) {
        dispatch(followersActions.follow(userId));
      } else {
        dispatch(followersActions.unfollow(userId));
      }
    },
    [dispatch],
  );

  const decryptKey = decryptKeys[-SubscriptionType.Adult] || '';
  const currentLang = useCurrentLang();

  const data = useMemo(() => {
    let matches;
    let objectType = '';
    let label: ReactNode = '';
    let footer: ReactNode = '';
    let description: string = '';
    const names: string[] = [];
    let preview: string[] = [];
    let LinkComponent: any = Link;
    let linkParams: any = {};
    const avatars: string[][] = [];
    let comment = '';
    let quote = '';
    let post: PostModel | null = null;
    let childPost: PostModel | null = null;
    let indicator: any = null;
    let chevron = false;
    let hasIcon = true;
    let attachments: AttachItem[] = [];
    let extra: any = null;

    if ((matches = notify.group.match(/post(\d+)(?:_(\d+))?/))) {
      if (posts[matches[1]]) {
        post = posts[matches[1]];
        preview = getImageFromPost(post, decryptKey);
      }

      if (posts[matches[2]]) {
        childPost = posts[matches[2]];
      }

      objectType = 'post';

      linkParams.name = `post${matches[1]}`;
      LinkComponent = ModalLink;
    } else if (notify.type === NotifyModelType.FOLLOW) {
      linkParams.routeName = PROFILE;
      linkParams.routeParams = { id: notify.extra };
    } else if (notify.type === NotifyModelType.FOLLOW_GROUPING) {
      linkParams.name = `followers${curUser.id}`;
      LinkComponent = ModalLink;
    }

    if (
      notify.extra &&
      notify.extra.userIds &&
      (notify.type === NotifyModelType.LIKE ||
        notify.type === NotifyModelType.FOLLOW_GROUPING)
    ) {
      for (let uid of notify.extra.userIds) {
        const user = users[uid];
        if (user) {
          const userNote = notes.find((item) => item.userId === user?.id);

          const name = userNote?.name
            ? userNote?.name
            : `${user.firstName} ${user.lastName}`;

          names.push(
            userNote?.name
              ? userNote?.name
              : currentLang.key !== 'ru'
              ? transliterate(name)
              : name,
          );
          avatars.push([user.photo, user.thumbnail]);
        }
      }

      if (names.length === 1) {
        label = <strong>{names[0]}</strong>;
      } else if (names.length === 2) {
        label = (
          <>
            <strong>{names[0]}</strong> <i>{t('and')}</i> <strong>{names[1]}</strong>
          </>
        );
      } else if (names.length === 3) {
        label = (
          <>
            <strong>
              {names[0]}, {names[1]}
            </strong>{' '}
            <i>{t('and')}</i> <strong>{names[2]}</strong>
          </>
        );
      } else {
        label = (
          <>
            <strong>
              {names[0]}, {names[1]}
            </strong>{' '}
            <i>{t('and_more')}</i>{' '}
            <strong>{t('people_count', { count: notify.extra.count - 2 })}</strong>
          </>
        );
      }
    }

    if (notify.type === NotifyModelType.LIKE) {
      if (post?.text) {
        quote = parsePostText(post.text, 100);
      }

      if (names.length === 3) {
        description = t('notify_liked');
      } else {
        description = t('notify_liked_count', { count: notify.extra.count });
      }

      if (objectType === NotifyModelType.POST) {
        if (post?.level === 0) {
          description += ` ${t('notify_comment_post')}`;
        } else {
          description += ` ${t('notify_comment_comment')}`;
        }
      }
    } else if (notify.type === NotifyModelType.FOLLOW) {
      const user = users[notify.extra];
      const userNote = notes.find((item) => item.userId === user?.id);
      const name = userNote?.name ? userNote?.name : `${user.firstName} ${user.lastName}`;
      label = userNote?.name
        ? userNote?.name
        : currentLang.key !== 'ru'
        ? transliterate(name)
        : name;
      avatars.push([user.photo, user.thumbnail]);
      description = genderText(user.sex, [
        t('notify_follow_male'),
        t('notify_follow_female'),
      ]);
      const isFollowed = following[user.id];
      indicator = (
        <div className="NotifyItem__follow">
          <Button
            icon={isFollowed ? <IcClose28 /> : <IcAdd28 />}
            size="small"
            mode={isFollowed ? 'secondary' : 'default'}
            onClick={handleFollow(isFollowed, user.id)}
          />
        </div>
      );
    } else if (notify.type === NotifyModelType.COMMENT) {
      const user = users[notify.extra.userId];
      const userNote = notes.find((item) => item.userId === user?.id);
      const name = userNote?.name ? userNote?.name : `${user.firstName} ${user.lastName}`;
      label = userNote?.name
        ? userNote?.name
        : currentLang.key !== 'ru'
        ? transliterate(name)
        : name;
      description =
        childPost?.level === 0
          ? t('notify_comment')
          : genderText(user.sex, [t('notify_reply_male'), t('notify_reply_female')]);
      avatars.push([user.photo, user.thumbnail]);
      comment = notify.extra.text;
      if (post) quote = parsePostText(post.text);
    } else if (notify.type === NotifyModelType.FOLLOW_GROUPING) {
      if (names.length === 3) {
        description = ` ${t('notify_followed')}`;
      } else {
        description = t('notify_followed_count', { count: notify.extra.count });
      }
    } else if (notify.type === NotifyModelType.TIPS) {
      const user = users[notify.extra.fromId];
      const userNote = notes.find((item) => item.userId === user?.id);
      const name = userNote?.name ? userNote?.name : `${user.firstName} ${user.lastName}`;
      avatars.push([user.photo, user.thumbnail]);
      label = userNote?.name
        ? userNote?.name
        : currentLang.key !== 'ru'
        ? transliterate(name)
        : name;
      const { amount, currency, postId } = notify.extra;
      const displayPrice =
        currency === CurrencyEnum.EUR
          ? `€${amount}`
          : `${amount} ${currency.toUpperCase()}`;
      if (postId) {
        description =
          genderText(user.sex, [
            t('notify_sent_tips_male'),
            t('notify_sent_tips_female'),
          ]) + ` ${displayPrice}`;
      } else {
        description =
          genderText(user.sex, [
            t('notify_sent_tips_post_male'),
            t('notify_sent_tips_post_female'),
          ]) + ` ${displayPrice}`;
      }
      linkParams.routeName = CHAT;
      linkParams.routeParams = {
        peerId: notify.extra.fromId,
      };

      if (postId) {
        extra = (
          <Button
            size="small"
            mode="secondary"
            onClick={(e) => {
              e.preventDefault();
              showModal(`post${postId}`);
            }}
          >
            {t('ads_view_open_post')}
          </Button>
        );
      }

      indicator = (
        <div className="NotifyItem__indicator">
          <Button icon={<IcBubble24 />} size="small" />
        </div>
      );
    } else if (notify.type === NotifyModelType.SOLD_MESSAGES) {
      const user = users[notify.extra.toId];
      const userNote = notes.find((item) => item.userId === user?.id);
      const name = userNote?.name ? userNote?.name : `${user.firstName} ${user.lastName}`;
      avatars.push([user.photo, user.thumbnail]);
      label = userNote?.name
        ? userNote?.name
        : currentLang.key !== 'ru'
        ? transliterate(name)
        : name;
      const { price, currency } = notify.extra;
      const displayPrice =
        currency === CurrencyEnum.EUR
          ? `€${price}`
          : `${price} ${currency.toUpperCase()}`;
      description =
        genderText(user.sex, [
          t('notify_sold_messages_male'),
          t('notify_sold_messages_female'),
        ]) + ` ${displayPrice}`;
      linkParams.routeName = CHAT;
      linkParams.routeParams = {
        peerId: notify.extra.toId,
      };
      indicator = (
        <div className="NotifyItem__indicator">
          <Button icon={<IcBubble24 />} size="small" />
        </div>
      );
      attachments = notify.extra.attachments;
    } else if (notify.type === NotifyModelType.NEW_SUBSCRIBERS) {
      const user = users[notify.extra.userId];
      if (user) {
        const userNote = notes.find((item) => item.userId === user?.id);
        const name = userNote?.name
          ? userNote?.name
          : `${user.firstName} ${user.lastName}`;
        avatars.push([user.photo, user.thumbnail]);
        label = userNote?.name
          ? userNote?.name
          : currentLang.key !== 'ru'
          ? transliterate(name)
          : name;
        description = genderText(user.sex, [
          t('notify_new_sponsor_male'),
          t('notify_new_sponsor_female'),
        ]);
        const isFollowed = following[user.id];
      }
      linkParams.routeName = CHAT;
      linkParams.routeParams = {
        peerId: notify.extra.userId,
      };

      indicator = (
        <div className="NotifyItem__indicator NotifyItem__follow">
          <Button
            onClick={() => goTo(linkParams.routeName, linkParams.routeParams)}
            icon={<IcBubble24 />}
            size="small"
          />
        </div>
      );
    } else if (notify.type === NotifyModelType.NEW_DEVICE) {
      label = t('notify_login_new_device');
      description = t('notify_login_new_device_desc');
      linkParams.routeName = SESSIONS;
      chevron = true;
    } else if (notify.type === NotifyModelType.ENDED_SUBSCRIPTION) {
      const isAuthor = !!notify?.extra?.authorId;
      if (isAuthor) {
        const user = users[notify.extra.authorId];
        let fullName = '';
        if (currentLang.key !== LanguageTags.ru) {
          fullName = transliterate(
            [user.firstName, user.lastName].filter(Boolean).join(' '),
          );
        } else {
          const name = incline(
            { first: user?.firstName ?? '', last: user?.lastName ?? '' },
            'genitive',
          );
          fullName = [name.first, name.last].filter(Boolean).join(' ');
        }
        avatars.push([user.photo, user.thumbnail]);
        label = t('notify_sponsorship_expired', {
          name: fullName,
        });
        description = t('notify_sponsorship_expired_desc');
        linkParams.routeName = SUBSCRIPTION_AUTHOR;
        hasIcon = false;
        linkParams.routeParams = {
          authorId: user.id,
        };
      } else {
        label = t('notify_adult_subscription_expired');
        linkParams.routeName = SUBSCRIPTION_ADULT;
        description = t('notify_adult_subscription_expired_desc');
      }
      footer = (
        <Button fillColor={isAuthor ? 'red' : 'purple'} size="medium">
          {isAuthor
            ? t('notify_sponsorship_expired_button')
            : t('notify_adult_subscription_expired_button')}
        </Button>
      );
    } else if (notify.type === NotifyModelType.ROULETTE_MESSAGES) {
      const user = users[notify.extra.toId];
      const userNote = notes.find((item) => item.userId === user?.id);
      const name = userNote?.name ? userNote?.name : `${user.firstName} ${user.lastName}`;
      avatars.push([user.photo, user.thumbnail]);
      label = userNote?.name
        ? userNote?.name
        : currentLang.key !== 'ru'
        ? transliterate(name)
        : name;
      // label = t('notify_roulette_messages');
      description = t('notify_roulette_messages_description', {
        amount: formatCurrency(notify.extra.price, notify.extra.currency),
      });
      const attachments = notify.extra.attachments as AttachItem[];
      const photo = (attachments || []).filter(
        (item) => item.type === AttachType.PHOTO,
      )[0]?.photo as AttachPhoto;

      const lot = (attachments || []).filter(
        (item) => item.type === AttachType.ROULETTE_LOT,
      )[0]?.rouletteLot as AttachRouletteLot;

      if (photo) {
        preview = [photo.photoLarge, photo?.thumbnail || ''];
      }

      if (lot) {
        quote = lot.title;
      }

      linkParams.routeName = CHAT;
      linkParams.routeParams = {
        peerId: notify.extra.toId,
      };
    } else if (notify.type === NotifyModelType.BUNDLE_SOLD && notify.extra.buyerId) {
      const user = users[notify.extra.buyerId];
      const userNote = notes.find((item) => item.userId === user?.id);
      const name = userNote?.name ? userNote?.name : `${user.firstName} ${user.lastName}`;
      avatars.push([user.photo, user.thumbnail]);
      const price = formatCurrency(notify.extra.bundle.price, CurrencyEnum.EUR);
      description = genderText(user.sex, [
        t('notify_bundle_sold_male', { price }),
        t('notify_bundle_sold_female', { price }),
      ]);
      linkParams.routeName = CHAT;
      linkParams.routeParams = { peerId: notify.extra.buyerId };
      label = userNote?.name
        ? userNote?.name
        : currentLang.key !== 'ru'
        ? transliterate(name)
        : name;
      indicator = (
        <div className="NotifyItem__indicator">
          <Button icon={<IcBubble24 />} size="small" />
        </div>
      );
    } else if (notify.type === NotifyModelType.GIFT_SOLD) {
      const user = users[notify.extra.toId];
      const userNote = notes.find((item) => item.userId === user?.id);
      const name = userNote?.name ? userNote?.name : `${user.firstName} ${user.lastName}`;
      avatars.push([user.photo, user.thumbnail]);
      label = userNote?.name
        ? userNote?.name
        : currentLang.key !== 'ru'
        ? transliterate(name)
        : name;
      const { price, currency } = notify.extra;
      const displayPrice =
        currency === CurrencyEnum.EUR
          ? `€${price}`
          : `${price} ${currency.toUpperCase()}`;
      description =
        genderText(user.sex, [
          t('notify_gift_sold_male'),
          t('notify_gift_sold_female'),
        ]) + ` ${displayPrice}`;
      linkParams.routeName = CHAT;
      linkParams.routeParams = {
        peerId: notify.extra.toId,
      };
      indicator = (
        <div className="NotifyItem__indicator">
          <Button icon={<IcBubble24 />} size="small" />
        </div>
      );
      attachments = notify.extra.attachments;
    } else if (notify.type === NotifyModelType.SUBSCRIPTION_WAS_ENDING_SOON) {
      // TODO
    }

    return {
      label,
      linkParams,
      LinkComponent,
      avatars,
      comment,
      indicator,
      description,
      quote,
      preview,
      chevron,
      footer,
      hasIcon,
      type: notify.type,
      attachments,
      extra,
    };
  }, [notify, posts, users, following, t]);

  const handleClick = useCallback((e) => {
    if (e.target.closest('.NotifyItem__follow')) {
      e.preventDefault();
      return false;
    }
  }, []);

  const LinkComponent = data.LinkComponent;

  return data.label ? (
    <LinkComponent className="NotifyItem" {...data.linkParams} onClick={handleClick}>
      <div className="NotifyItem__body">
        {data.avatars.length > 0 ? (
          <div
            className={classNames('NotifyItem__avatars', {
              multi: data.avatars.length > 1,
              single: data.avatars.length == 1,
            })}
          >
            {data.avatars.slice(0, 2).map((photo) => (
              <div
                className="NotifyItem__avatars__item"
                style={{
                  backgroundImage: photo
                    .filter(Boolean)
                    .map((p) => `url(${p})`)
                    .join(', '),
                }}
                key={photo[0]}
              />
            ))}
            {data.hasIcon && <NotifyItemIcon type={data.type} />}
          </div>
        ) : (
          <NotifyItemIcon type={data.type} />
        )}
        <div className="NotifyItem__cont">
          <div className="NotifyItem__label">{data.label}</div>
          <div className="NotifyItem__addition">
            <div className="NotifyItem__description">{data.description}</div>
            {data.attachments && (
              <Attachments
                parentObject="notify"
                decryptKey=""
                attachments={data.attachments}
              />
            )}
            {!!notify.createdAt && <div className="NotifyItem__date">{date}</div>}
          </div>
          {data.quote && (
            <div className="NotifyItem__quote">
              <Truncate
                lines={2}
                limit={isDesktop ? 115 : 60}
                text={data.quote}
                expandText={t('notify_text_expand')}
              />
            </div>
          )}
          {data.comment && (
            <div className="NotifyItem__comment">
              <Truncate
                lines={2}
                limit={isDesktop ? 95 : 65}
                text={data.comment}
                expandText={t('notify_text_expand')}
              />
            </div>
          )}
          {!!data.extra && <div className="NotifyItem__extra">{data.extra}</div>}
        </div>
        {data.preview.length > 0 && (
          <div
            className="NotifyItem__preview"
            style={{
              backgroundImage: data.preview.map((p) => `url(${p})`).join(', '),
            }}
          ></div>
        )}
        {data.chevron && (
          <div className="NotifyItem__chevron">
            <IcAngleRight16 />
          </div>
        )}
        {data.indicator}
      </div>
      {data.footer && <div className="NotifyItem__footer">{data.footer}</div>}
    </LinkComponent>
  ) : null;
};
